/* eslint-disable camelcase */
import React, { useEffect, useState, useContext, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMutation, useQueryClient } from 'react-query';
import { isEqual } from 'lodash';
import { func } from 'prop-types';
import {
  Button,
  CircularProgress,
  Grid,
  InputLabel,
  TextField,
  MenuItem,
  Select,
} from '@nextiva/next-ui-components';
import { dataSelector } from '@nextiva/global-store';
import clsx from 'clsx';
import {
  RootContext,
  actionTypes as rootActionTypes,
} from 'RootContext/rootContext';
import { postUsers, getUser } from 'api/userApi';
import { assignUserLicenses } from 'api/licenseService';
import { useRootUserList } from 'customHooks/userCustomHooks';
import { useCreateCXUser } from 'hooks/useUpdateCXUser/useUpdateCXUser';
import { FeatureContext } from 'components/AccessResolvers/featureContext';
import Hint from 'components/Hint';
import {
  UserContext,
  actionTypes,
} from 'containers/pages/ManageUsers/usersContext';
import { useSnackbar, useGetTimezoneOptions } from 'hooks';
import { validData, isValidEmail } from 'utils/validators';
import { getLocationName } from 'utils/display-helpers';
import { assignedLicensesFeatureList } from 'utils/license-helpers';
import {
  ADMINISTRATION_ACCESS,
  ROLE,
  EMAIL_ADDRESS,
  PRIMARY_TEAM,
  ADMIN_LOCATIONS,
  ROLE_FOR_OTHER_LOCATIONS,
} from 'constants/toolTips';
import { CRM_LICENSES } from 'constants/crmLicenses';
import EmailErrorMessage from 'containers/pages/ManageUsers/EmailErrorMessage';

import { USER_UPDATE } from 'containers/pages/ManageUsers/duck/types';
import { isLicenseFeatureAssigned } from 'utils/checkForAssignedFeature';
import { WorkspaceChipSelect } from 'components/WorkspaceChipSelect/WorkspaceChipSelect';
import { UserDrawerContext } from '../UserDrawerContent/UserDrawerContext';
import { LicenseSection, Section } from '../EditUser/Sections';
import { adminAccessOptions } from '../adminAccessOptions';
import styles from './m-create-user.scss';

const buttonStyle = {
  width: '110px',
  height: '40px',
};

const listOfValidatedFields = ['first_name', 'last_name', 'email'];
const initialUser = {
  first_name: '',
  last_name: '',
  email: '',
  admin_access: '',
  admin_locations: [],
  role: '',
  team: '',
  locationId: 0,
  preferred_time_zone: 0,
};

const LICENSES_LINK = 'apps/platform-admin/#/licensing/licenses';

/**
 * Create User Drawer content component. Contains input fields needed to create
 * a user.
 *
 * @param {props} props: Props of the component
 *  @property toggleDrawer: function that enables the opening and closing of the
 * drawer
 */
export const CreateUser = ({ toggleDrawer }) => {
  const queryClient = useQueryClient();
  const {
    featureState: {
      authV3Enabled,
      locationBasedAdmin: locationBasedAdminFeature,
      globalNextivaCXFlowEnabled,
    },
    corpAcctNbr,
  } = useContext(FeatureContext);
  const {
    userState: {
      userFilterStatus,
      teamsOptions,
      rolesOptions,
      locationsOptions,
      notifyModal: { scrollToBottom, createUser },
      baseUserList,
      newCXUserWorkspaces,
    },
    dispatch,
  } = useContext(UserContext);
  const {
    rootState: {
      drawerSnackbar: { isOpen },
    },
    rootDispatch,
  } = useContext(RootContext);
  const {
    userDrawerState: { licensesToAdd },
  } = useContext(UserDrawerContext);
  const timezoneOptions = useGetTimezoneOptions();
  const { licenses } = useSelector(dataSelector);

  const requirePrimaryTeam = licenses
    .find((lic) => lic.product === 'CRM')
    ?.features.some((permission) => CRM_LICENSES.includes(permission));
  const hasCXFeatureAssigned =
    isLicenseFeatureAssigned(licensesToAdd, 'CXSocialStandard') ||
    isLicenseFeatureAssigned(licensesToAdd, 'CXSocialGrowth');
  const isWorkspaceFlowEnabled =
    globalNextivaCXFlowEnabled && hasCXFeatureAssigned;
  if (requirePrimaryTeam && !authV3Enabled) {
    listOfValidatedFields.push('team');
  }
  const setSnackbar = useSnackbar(true);

  const [sending, setSending] = useState(false);
  const [userInput, setUserInput] = useState(initialUser);
  const [errors, setErrors] = useState({});
  const [saveable, setSavable] = useState(false);
  const [uuid, setUuid] = useState('');
  const newUserProfileId = useRef(null);
  const { updateRootUserList } = useRootUserList();
  const createUserDom = useRef(null);
  const dispatchUser = useDispatch();

  const createCXUser = useCreateCXUser();

  const usersEmailsList = baseUserList.map(({ email }) => email);

  const superAdminRole = rolesOptions.find(
    (role) => role.roleType === 'SUPER_ADMIN',
  );

  const rolesTool =
    userInput.admin_access === 'certain_loc_admin'
      ? 'Role (for other locations)'
      : 'Role';

  const rolesTip =
    userInput.admin_access === 'certain_loc_admin'
      ? ROLE_FOR_OTHER_LOCATIONS
      : ROLE;

  const filteredRolesOptions =
    userInput.admin_access === 'certain_loc_admin'
      ? rolesOptions.filter((role) => role.roleType !== 'SUPER_ADMIN')
      : rolesOptions;

  useEffect(() => {
    if (scrollToBottom && createUserDom.current) {
      createUserDom.current.scrollIntoView(false);
    }

    return dispatch({
      type: actionTypes.SET_NOTIFY_MODEL,
      notifyModal: { scrollToBottom: false },
    });
  }, [scrollToBottom]);

  useEffect(() => {
    setSavable(
      listOfValidatedFields.filter((e) => errors[e]).length === 0 &&
        listOfValidatedFields.filter((e) => e in errors).length ===
          listOfValidatedFields.length,
    );
  }, [errors, saveable]);

  const handleError = ({ id, hasError }) => {
    setErrors({
      ...errors,
      [id]: hasError,
    });
  };

  const handleInputChange = ({ target }) => {
    const { id, value } = target;

    setUserInput({
      ...userInput,
      [id]: value,
    });

    const textFields = ['first_name', 'last_name'];

    let err;

    if (textFields.includes(id)) {
      const limit = 30;

      err = {
        id,
        hasError: validData(limit, value),
      };

      handleError(err);
    } else if (id === 'email') {
      err = {
        id,
        hasError: isValidEmail(value)
          ? usersEmailsList.includes(value)
            ? 'User with this email already exists'
            : ''
          : EmailErrorMessage,
      };

      handleError(err);
    } else if (
      ['preferred_time_zone', 'role', 'team', 'locationId'].includes(id)
    ) {
      if (value !== 0) {
        const error = {
          id,
          hasError: false,
        };

        handleError(error);
      }
    }
  };

  const handleSelectChange = (e, id) => {
    e.target.id = id;
    handleInputChange(e);
  };

  const handelSelectAdminAccess = ({ target }) => {
    const { value } = target;
    const newUserInput = {
      ...userInput,
      admin_access: value,
    };

    if (value === 'entire_acc_admin') {
      newUserInput.role = superAdminRole.value;
    } else {
      newUserInput.role = '';
    }

    setUserInput(newUserInput);
  };

  const { mutateAsync: postUsersAsync } = useMutation(({ userObj }) =>
    postUsers(userObj, authV3Enabled),
  );

  const postUserAwait = (newUser) => {
    return postUsersAsync({ userObj: newUser })
      .then((response) => {
        setUuid(response?.userUuid);
        if (licensesToAdd.length === 0) updateRootUserList();
        newUserProfileId.current = response.profile_id ?? response.id;

        const dateTime = new Date();

        // if userlist showing inactive users, set sorting to
        // default after user created
        if (!userFilterStatus) {
          dispatch({
            type: actionTypes.SET_FILTER_STATUS,
            userFilterStatus: true,
          });

          dispatch({
            type: actionTypes.FILTER_USER_LIST,
            authV3Enabled,
          });

          dispatch({
            type: actionTypes.UPDATE_TABLE_SORT,
            sortFieldType: 'string',
            sortField: authV3Enabled ? 'firstName' : 'first_name',
            sortOrder: 'asc',
          });
        }

        // Find the new role name from the options
        const roleIndex = rolesOptions.findIndex(
          (ele) => ele.key === userInput.role,
        );

        // Set the role display for optimistic update
        userInput.roleDisplay = '';
        userInput.roles = [];

        // Check that the role option was found
        if (roleIndex !== -1) {
          const newRole = rolesOptions[roleIndex];

          userInput.roleDisplay = newRole.label;
          userInput.roles = [
            {
              id: newRole.key,
              name: newRole.label,
              description: newRole.description,
            },
          ];
        }

        userInput.locationDisplay = getLocationName(
          locationsOptions,
          parseInt(userInput.locationId, 10),
        );

        // Get full team object
        const detailedTeam = teamsOptions.find(
          (team) => team.key === userInput.team,
        );

        const createdUserForList = {
          active: true,
          nextiva_uuid: '',
          first_name: userInput.first_name,
          last_name: userInput.last_name,
          email: userInput.email,
          team: [
            {
              id: detailedTeam?.key,
              name: detailedTeam?.label,
              description: detailedTeam?.description,
            },
          ],
          teamDisplay: detailedTeam?.label,
          roles: userInput.roles,
          roleDisplay: userInput.roleDisplay,
          status: true,
          profiles: [
            {
              id: response.profile_id,
              active: true,
              locationId: parseInt(userInput.locationId, 10),
              nextOS: {
                primary_identifier: '',
              },
            },
          ],
          locationDisplay: userInput.locationDisplay,
          timeZone: userInput.preferred_time_zone,
          createdDate: dateTime,
          updatedDate: dateTime,
          deactivatedDate: '',
          checked: false,
          statusDisplay: 'Active',
        };

        dispatch({
          type: actionTypes.ADD_USER_TO_USER_LIST,
          createdUser: createdUserForList,
        });

        return true;
      })
      .catch((response) => {
        const server_error_msg = response.errors?.msg;

        if (server_error_msg) {
          if (server_error_msg.includes('email')) {
            const err = { id: 'email', hasError: true };

            handleError(err);
          }

          setSnackbar({ message: server_error_msg, severity: 'error' });
        } else {
          setSnackbar({
            message: 'Unable to create new user. Please try again later.',
            severity: 'error',
          });
        }

        return false;
      });
  };

  const { mutateAsync: getUserAsync } = useMutation(({ email }) =>
    getUser(email, authV3Enabled, uuid),
  );

  const licensesRequestAwait = (email) => {
    return getUserAsync({ email })
      .then(({ user }) => {
        const requestBody = {
          licenses: licensesToAdd.map((license) => {
            return {
              license_id: license.id,
              expiration_date: license.expiration_date,
            };
          }),
          profile_id: user.profiles[0].id,
        };

        return {
          requestBody,
          nextiva_uuid: user.nextiva_uuid || user.nextivaUUID,
        };
      })
      .catch(() => {
        setSnackbar({
          message:
            'Unable to assign licenses to new user. Please try again later.',
          severity: 'error',
        });

        return false;
      });
  };

  const { mutateAsync: assignUserLicensesAsync } = useMutation(
    ({ requestBody, nextiva_uuid }) => {
      return assignUserLicenses(
        authV3Enabled
          ? requestBody.licenses.map((license) => license.license_id)
          : requestBody,
        authV3Enabled,
        corpAcctNbr,
        nextiva_uuid,
      );
    },
  );

  const assignLicensesAwait = (requestBody, nextiva_uuid) => {
    return assignUserLicensesAsync({ requestBody, nextiva_uuid })
      .then(() => {
        return true;
      })
      .catch(() => {
        setSnackbar({
          message:
            'Unable to assign licenses to new user. Please try again later.',
          severity: 'error',
        });

        return false;
      });
  };

  const buildUserCrmObj = () =>
    authV3Enabled
      ? {
          email: userInput.email,
          firstName: userInput.first_name,
          lastName: userInput.last_name,
          ...(userInput.locationId !== 0 && {
            locationId: parseInt(userInput.locationId, 10),
          }),
          ...(userInput.preferred_time_zone && {
            preferredTimeZone: userInput.preferred_time_zone,
          }),
          roleTeamIds: [
            ...(userInput.team ? [userInput.team] : []),
            ...(userInput.role ? [userInput.role] : []),
          ],
        }
      : {
          first_name: userInput.first_name,
          last_name: userInput.last_name,
          email: userInput.email,
          ...(userInput.locationId !== 0 && {
            locationId: parseInt(userInput.locationId, 10),
          }),
          ...(userInput.preferred_time_zone && {
            preferred_time_zone: userInput.preferred_time_zone,
          }),
          add_ids: [
            ...(userInput.team ? [userInput.team] : []),
            ...(userInput.role ? [userInput.role] : []),
          ],
        };

  const saveSuccessNotification = (text) => {
    const { length } = licensesToAdd;
    const message =
      text === 'user'
        ? 'User was successfully created!'
        : `License${length > 1 ? 's were' : ' was'} successfully assigned!`;

    if (text === 'user' && length > 0) return;
    setSnackbar({ message, severity: 'success' });
    setTimeout(() => {
      toggleDrawer();
    }, 4000);
  };

  const createNewUserAsync = async (event, unsavedChangesFlow = false) => {
    setSending(true);
    const newUser = buildUserCrmObj();

    const postUser = await postUserAwait(newUser);

    if (!postUser) return setSending(false);

    saveSuccessNotification('user');
    if (licensesToAdd.length > 0) {
      const { requestBody, nextiva_uuid } = await licensesRequestAwait(
        userInput.email,
      );

      if (!requestBody) return setSending(false);
      const assignLicenses = await assignLicensesAwait(
        requestBody,
        nextiva_uuid,
      );

      if (!assignLicenses) return setSending(false);
      const licenseFeatures = assignedLicensesFeatureList(licensesToAdd);
      const isLicensesVoice = licenseFeatures.some(
        (f) => f === 'PLATFORM_VOICE_ACCESS',
      );
      saveSuccessNotification('license');
      setSending(false);

      if (isWorkspaceFlowEnabled)
        createCXUser.mutate({
          userUuid: nextiva_uuid,
          role: userInput?.roleDisplay,
        });

      if (isLicensesVoice) {
        setTimeout(() => {
          window.location.hash = `#/users/thankyou?profileId=${newUserProfileId.current}`;
        }, 2000);
      } else {
        dispatchUser({ type: USER_UPDATE, payload: true });
      }
      queryClient.invalidateQueries(['licenseList']);
    }
    setSending(false);
    if (userInput.locationId) queryClient.invalidateQueries(['locations']);
    if (unsavedChangesFlow) {
      setTimeout(() => {
        window.location = `${window.location.origin}/${LICENSES_LINK}`;
      }, 2000);
    }
  };

  const validateUnsavedChanges = () => {
    const validationErrors = {};
    const textFieldLimit = 30;

    validationErrors.first_name = validData(
      textFieldLimit,
      userInput?.first_name,
    );
    validationErrors.last_name = validData(
      textFieldLimit,
      userInput?.last_name,
    );
    validationErrors.email = isValidEmail(userInput?.email)
      ? usersEmailsList.includes(userInput?.email)
        ? 'User with this email already exists'
        : ''
      : EmailErrorMessage;
    validationErrors.role = userInput?.role ? '' : 'required';
    validationErrors.team = userInput?.team ? '' : 'required';

    setErrors(validationErrors);
    return validationErrors;
  };

  const saveUnsavedChanges = (unsavedChangesFlow) => {
    const validationErrors = validateUnsavedChanges();

    if (
      !validationErrors?.first_name &&
      !validationErrors?.last_name &&
      !validationErrors?.email &&
      !validationErrors?.role &&
      !validationErrors?.team
    )
      createNewUserAsync(null, unsavedChangesFlow);
  };

  useEffect(() => {
    if (createUser) createNewUserAsync();

    return dispatch({
      type: actionTypes.SET_NOTIFY_MODEL,
      notifyModal: { createUser: false },
    });
  }, [createUser]);

  const onCreateLicenseCheck = () => {
    if (isWorkspaceFlowEnabled) {
      const canSaveWorkspaces = newCXUserWorkspaces.length !== 0;
      if (!canSaveWorkspaces) {
        setSnackbar({
          message: 'Please assign a workspace to this user to continue.',
          severity: 'error',
        });
        rootDispatch({ type: rootActionTypes.WORKSPACE_ERROR, payload: true });
        return;
      }
    }

    if (licensesToAdd.length === 0) {
      dispatch({
        type: actionTypes.SET_NOTIFY_MODEL,
        notifyModal: {
          open: true,
        },
      });

      return;
    }

    createNewUserAsync();
  };

  const teamOptionsMenuItems = teamsOptions.map(({ key, label }) => ({
    value: key,
    key,
    label,
  }));

  return (
    <div ref={createUserDom} className="m-create-user">
      <Grid container spacing={0}>
        <Grid item xs={12}>
          <div className={styles.fieldPadding}>
            <div
              className={clsx('Heading6', styles.subHeader)}
              data-testid="PlatformAdminUI-UsersLink-CreateUser-Description"
            >
              Add a new user to your Nextiva account by sending them an email.
            </div>
          </div>
        </Grid>

        <Grid item xs={12}>
          <div className={styles.fieldPadding}>
            <TextField
              id="first_name"
              inputProps={{
                'data-testid': 'PlatformAdminUI-UsersLink-CreateUser-FirstName',
              }}
              fullWidth
              helperText={
                !!errors?.first_name && `First name ${errors?.first_name}`
              }
              label="First Name"
              value={userInput.first_name}
              error={!!errors?.first_name}
              onChange={handleInputChange}
              data-testid="PlatformAdminUI-CreateUser-TextFieldFirstName"
            />
          </div>
        </Grid>

        <Grid item xs={12}>
          <div className={styles.fieldPadding}>
            <TextField
              id="last_name"
              fullWidth
              helperText={
                !!errors?.last_name && `Last name ${errors?.last_name}`
              }
              label="Last Name"
              value={userInput.last_name}
              error={!!errors?.last_name}
              onChange={handleInputChange}
              inputProps={{
                'data-testid': 'PlatformAdminUI-UsersLink-CreateUser-LastName',
              }}
              data-testid="PlatformAdminUI-CreateUser-TextFieldLastName"
            />
          </div>
        </Grid>

        <Grid item xs={12}>
          <div className={styles.fieldPadding}>
            <TextField
              fullWidth
              id="email"
              helperText={!!errors?.email && errors?.email}
              label={<Hint tool="Email (User Id)" tip={EMAIL_ADDRESS} />}
              value={userInput.email}
              error={!!errors?.email}
              onChange={handleInputChange}
              inputProps={{
                'data-testid': 'PlatformAdminUI-UsersLink-CreateUser-Email',
              }}
              data-testid="PlatformAdminUI-CreateUser-TextFieldEmail"
            />
          </div>
        </Grid>

        {locationBasedAdminFeature && (
          <>
            <Grid item xs={12}>
              <div className={styles.fieldPadding}>
                <InputLabel id="AdminAccess-Option">
                  <Hint
                    tool="Administration access"
                    tip={ADMINISTRATION_ACCESS}
                  />
                </InputLabel>
                <Select
                  labelId="AdminAccess-Option"
                  id="admin_access"
                  onChange={handelSelectAdminAccess}
                  value={userInput.admin_access}
                  fullWidth
                  data-testid="PlatformAdminUI-CreateUser-SelectAdminAccess"
                >
                  {adminAccessOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </div>
            </Grid>

            {userInput.admin_access === 'certain_loc_admin' && (
              <Grid item xs={12}>
                <div className={styles.fieldPadding}>
                  <InputLabel id="AdminLocations-Options">
                    <Hint tool="Admin location(s)" tip={ADMIN_LOCATIONS} />
                  </InputLabel>
                  <Select
                    labelId="AdminLocations-Options"
                    id="admin_locations"
                    multiple
                    onChange={(e) => handleSelectChange(e, 'admin_locations')}
                    value={userInput.admin_locations}
                    fullWidth
                    data-testid="PlatformAdminUI-CreateUser-SelectAdminLocations"
                  >
                    {locationsOptions.map((option) => (
                      <MenuItem key={option.key} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </div>
              </Grid>
            )}
          </>
        )}

        <Grid item xs={12}>
          <div className={styles.fieldPadding}>
            <InputLabel id="Roles-Option">
              <Hint tool={rolesTool} tip={rolesTip} />
            </InputLabel>
            <Select
              labelId="Roles-Option"
              id="roles"
              disabled={userInput.admin_access === 'entire_acc_admin'}
              onChange={(e) => handleSelectChange(e, 'role')}
              value={userInput.role}
              fullWidth
              error={!!errors?.role}
              errorMessage={`Role ${errors?.role}`}
              data-testid="PlatformAdminUI-CreateUser-SelectRoles"
            >
              {filteredRolesOptions.map((roleOption) => (
                <MenuItem key={roleOption.key} value={roleOption.value}>
                  {roleOption.label}
                </MenuItem>
              ))}
            </Select>
          </div>
        </Grid>
        {isWorkspaceFlowEnabled && (
          <WorkspaceChipSelect flowType="CREATE_USER" />
        )}
        {requirePrimaryTeam && !authV3Enabled && (
          <Grid item xs={12}>
            <div className={styles.fieldPadding}>
              <InputLabel id="Teams-Option">
                <Hint tool="Primary Team" tip={PRIMARY_TEAM} />
              </InputLabel>
              <Select
                labelId="Teams-Option"
                id="teams"
                onChange={(e) => handleSelectChange(e, 'team')}
                value={userInput.team}
                error={!!errors?.team}
                errorMessage="Please select a team."
                fullWidth
                data-testid="PlatformAdminUI-CreateUser-SelectTeams"
              >
                {teamOptionsMenuItems.map((option) => (
                  <MenuItem key={option.key} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </div>
          </Grid>
        )}

        <Grid item xs={12}>
          <div className={styles.fieldPadding}>
            <InputLabel id="Locations-Option">LOCATION (optional)</InputLabel>
            <Select
              labelId="Locations-Option"
              id="location"
              onChange={(e) => handleSelectChange(e, 'locationId')}
              value={userInput.locationId || ''}
              className={styles.root}
              error={!!errors?.location}
              errorMessage="Please select a location."
              fullWidth
              data-testid="PlatformAdminUI-CreateUser-SelectLocation"
            >
              {locationsOptions.map((opt) => (
                <MenuItem key={opt.key} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </div>
        </Grid>

        <Grid item xs={12}>
          <div className={styles.fieldPadding}>
            <InputLabel id="TimeZone-Option">TIME ZONE (optional)</InputLabel>
            <Select
              labelId="TimeZone-Option"
              onChange={(e) => {
                handleSelectChange(e, 'preferred_time_zone');
              }}
              className={styles.root}
              value={userInput.preferred_time_zone || ''}
              fullWidth
              id="preferred_time_zone"
              data-testid="PlatformAdminUI-CreateUser-SelectTimeZone"
            >
              {timezoneOptions.data?.map((option) => (
                <MenuItem key={option.key} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </div>
        </Grid>

        <Grid item xs={12}>
          <Section id="licenses" title="Licenses">
            <LicenseSection
              unsavedUserChanges={!isEqual(initialUser, userInput)}
              saveChanges={saveUnsavedChanges}
            />
          </Section>
        </Grid>

        <div className="m-drawer-footer">
          <div className="m-drawer-footer__container">
            <div className="m-drawer-footer__container__action-bar">
              <div className="m-drawer-footer__container__action-bar__button">
                <Button
                  id="cancel-drawer"
                  style={buttonStyle}
                  color="secondary"
                  variant="contained"
                  onClick={toggleDrawer}
                  fullWidth
                  disabled={sending}
                  data-testid="PlatformAdminUI-UsersLink-CreateUser-CancelButton"
                >
                  Cancel
                </Button>
              </div>
              <div className="m-drawer-footer__container__action-bar__button">
                <Button
                  id="submit-button"
                  color="primary"
                  variant="contained"
                  disabled={!saveable || sending || !userInput.role || isOpen}
                  onClick={onCreateLicenseCheck}
                  style={{
                    ...buttonStyle,
                    color: 'white',
                  }}
                  fullWidth
                  data-testid="PlatformAdminUI-UsersLink-CreateUser-CreateButton"
                >
                  Create
                </Button>
              </div>
              {sending && (
                <div className="m-drawer-footer__container__action-bar__spinner">
                  <CircularProgress
                    style={{ color: '#009FE0' }}
                    variant="indeterminate"
                    size={36}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </Grid>
      <div className={styles.spacer} />
    </div>
  );
};

CreateUser.propTypes = {
  toggleDrawer: func.isRequired,
};

export default CreateUser;
